import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import GpxList from "../components/GpxList";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import * as turf from "@turf/turf";

// Theme Images
import arrowRight from '../theme/img/angle-right.svg';
import trustPilot from '../theme/img/trustpilot.svg';
import shadow from '../theme/img/shadow.svg';

// State
import { setGeoJson } from "../actions";

const polyline = require('@mapbox/polyline');

const StravaList = ({ user, returnTokens, setGeoJson, geoJson }) => {
    const history = useHistory();
    const callActivities = `https://www.strava.com/api/v3/athlete/activities?access_token=`;

    const [activities, setActivities] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedActivities, setSelectedActivities] = useState([]);

    
    const getActivities = (access) => {
        // Request activities with polyline data
        fetch(`https://www.strava.com/api/v3/athlete/activities?access_token=${access}&per_page=50`)
        .then(res => res.json())
        .then(data => {
            console.log("Activities received:", data);
            // For each activity, fetch detailed data including polyline
            const detailedActivities = data.map(async (activity) => {
                try {
                    const detailResponse = await fetch(`https://www.strava.com/api/v3/activities/${activity.id}?access_token=${access}`);
                    const detailData = await detailResponse.json();
                    console.log(`Activity ${activity.name} detail:`, detailData);
                    return detailData;
                } catch (error) {
                    console.error(`Error fetching details for activity ${activity.id}:`, error);
                    return activity; // Return original activity if detail fetch fails
                }
            });
            
            Promise.all(detailedActivities).then(activities => {
                setActivities(activities);
                setIsLoading(false);
            });
        })
        .catch(e => {
            console.log("Error fetching activities:", e);
            setIsLoading(false);
        })
    }

    useEffect(() => {
        getActivities(returnTokens?.access_token)
      }, [])


    const handleNextStep = (e) => {
        e.preventDefault();
        if(selectedActivities.length > 0) {
            // Convert selected activities to GeoJSON format for the map
            const geoJsonFeatures = selectedActivities.map(activity => ({
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: activity.coords
                },
                properties: {
                    name: activity.name,
                    type: activity.type
                }
            }));
            
            console.log("Setting GeoJSON features:", geoJsonFeatures);
            console.log("Selected activities:", selectedActivities);
            
            // Store in Redux state
            console.log("About to dispatch setGeoJson action");
            setGeoJson(geoJsonFeatures);
            console.log("setGeoJson action dispatched");
            history.push('/products/design-your-own-trail-map/design');
        }
    }

    const handleAdd = (map) => {
        // Try to get polyline from different possible locations
        const polylineData = map.map?.summary_polyline || map.summary_polyline || map.polyline;
        
        if (!polylineData) {
            console.error("No polyline data found for activity:", map.name);
            alert(`Activity "${map.name}" doesn't have route data available. This might be a private activity or one without GPS tracking.`);
            return;
        }
        
        const coords = decodePolyline(polylineData);
        
        if (!coords || coords.length < 2) {
            console.error("Invalid coordinates for activity:", map.name, coords);
            alert(`Activity "${map.name}" has invalid route data.`);
            return;
        }
        
        const coordinates = coords.map(coord => [coord[1], coord[0]]); // lat, lng to lng, lat

        const activity = {
            name: map.name,
            coords: coordinates,
            distance: map.distance,
            total_elevation_gain: map.total_elevation_gain,
            moving_time: map.moving_time,
            type: map.type
        };

        setSelectedActivities(prev => {
            const newActivities = [...prev, activity];
            
            // Update geoJson immediately for map display
            const geoJsonFeatures = newActivities.map(act => ({
                type: "Feature",
                geometry: {
                    type: "LineString",
                    coordinates: act.coords
                },
                properties: {
                    name: act.name,
                    type: act.type
                }
            }));
            
            setGeoJson(geoJsonFeatures);
            
            return newActivities;
        });
    }

    const handleRemoveGpx = (index) => {
        setSelectedActivities(prev => {
            const newActivities = prev.filter((_, i) => i !== index);
            
            // Update geoJson immediately for map display
            if (newActivities.length > 0) {
                const geoJsonFeatures = newActivities.map(act => ({
                    type: "Feature",
                    geometry: {
                        type: "LineString",
                        coordinates: act.coords
                    },
                    properties: {
                        name: act.name,
                        type: act.type
                    }
                }));
                
                console.log("Updating map after removal:", geoJsonFeatures);
                setGeoJson(geoJsonFeatures);
            } else {
                console.log("No activities left, clearing map");
                setGeoJson(null);
            }
            
            return newActivities;
        });
    };

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const items = Array.from(selectedActivities);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update geoJson immediately for map display
        const geoJsonFeatures = items.map(act => ({
            type: "Feature",
            geometry: {
                type: "LineString",
                coordinates: act.coords
            },
            properties: {
                name: act.name,
                type: act.type
            }
        }));
        
        console.log("Updating map after reorder:", geoJsonFeatures);
        setGeoJson(geoJsonFeatures);
        
        setSelectedActivities(items);
    };

    const decodePolyline = (encodedString) => {
        if (!encodedString) return [];
        const decoded = polyline.decode(encodedString);

        return decoded;
    };

    return (
        <>
            <div className="step-title">
                <span>STEP 1</span>
                <div className="flex space-between ">
                    <h2>Upload your route</h2>
                    <a href="https://trailmaps.com/pages/help-with-the-design-your-own-map-builder" target="_blank">Help</a>
                </div>
            </div>
            <GpxList
                handleAdd={handleAdd}
                handleRemove={handleRemoveGpx}
                content={activities}
                selectedActivities={selectedActivities}
            />
            {selectedActivities.length > 0 && (
                <>
                    <div className="text-content">
                        <h3>Selected Activities - Drag to reorder</h3>
                    </div>
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                        <Droppable droppableId="selected-maps-list">
                            {(provided) => (
                                <div className="strava-maps-list maps-list map-order-list ui-sortable" id="selected-list" {...provided.droppableProps} ref={provided.innerRef}>
                                    {selectedActivities.map((activity, index) => (
                                        <Draggable key={`${activity.name}${index}`} draggableId={`${activity.name}${index}`} index={index}>
                                            {(provided) => (
                                                <div className="map-single flex space-between ui-sortable-handle" ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                    <div className="map-left">
                                                        <h3>{activity.name}</h3>
                                        <p>{activity.type}<br/>
                                        {activity.coords && activity.coords.length >= 2 ? 
                                            `${turf.length(turf.lineString(activity.coords), {units: 'kilometers'}).toFixed(2)}km` : 
                                            'No route data'
                                        }</p>
                                                        <button className="remove added added-list" onClick={() => handleRemoveGpx(index)}>
                                                            <svg width="14px" height="3px" viewBox="0 0 14 3" version="1.1" >
                                                                <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="square">
                                                                    <g id="Upload---02" transform="translate(-73.000000, -451.000000)" stroke="#D93708" stroke-width="2">
                                                                        <g id="Group-Copy" transform="translate(37.000000, 300.000000)">
                                                                            <g id="Group-4-Copy" transform="translate(43.000000, 152.500000) rotate(-90.000000) translate(-43.000000, -152.500000) translate(42.000000, 147.000000)">
                                                                                <line x1="1" y1="0" x2="1" y2="11" id="Line-Copy-2"></line>
                                                                            </g>
                                                                        </g>
                                                                    </g>
                                                                </g>
                                                            </svg>
                                                            <span>Remove</span>
                                                        </button>
                                                    </div>
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </>
            )}
            <div className="bottom-links">
            <div className="shadow">
                <img src={shadow} alt="shadow" />
            </div>
            <div className="next-step">
                <a href="#">
                    <div className={`connect flex space-between acenter ${/*!isValid && `opacity-5`*/''}`} onClick={handleNextStep}>
                        <div className="c-left">
                            <span>Next Step - Design</span>
                        </div>
                        <div className="c-right">
                            <img src={arrowRight} alt="right" />
                        </div>
                    </div>
                </a>
            </div>
            <div className="trust-pilot flex space-between acenter">
                <div className="tp-left">
                    <span>
                        <strong>Excellent</strong> | Trustscore 4.9 | <a href="#">152 reviews</a>
                    </span>
                </div>
                <div className="tp-left right">
                    <img src={trustPilot} alt="trust-pilot" />
                </div>
            </div>
        </div>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        user: state.user,
        returnTokens: state.returnTokens,
        strava: state.strava,
        stravaJson: state.stravaJson,
        geoJson: state.geoJson,
    };
};

export default connect(mapStateToProps, {
    setGeoJson,
})(StravaList);
